import React, { useState, useEffect } from "react";
import { getAddresses, createAddress, updateAddress, removeAddress } from "../api/address.api";
import { getStaffId } from "../utils/storage";
import { apiErrorMessage } from "../utils/apiError";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import EmptyState from "../Components/States/EmptyState";
import { FaEdit, FaTrash, FaPlus, FaMapMarkerAlt } from "react-icons/fa";
import { Helmet } from "react-helmet";
import ConfirmModal from "../Popups/ConfirmModal";
import LoadingState from "../Components/States/LoadingState";

const AddressPage = () => {
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    street: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    addressType: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const staffId = getStaffId();

  // Fetch addresses
  useEffect(() => {
    if (staffId) {
      getAddresses(staffId)
        .then((response) => {
          if (response.data && response.data.addresses) {
            setAddresses(response.data.addresses);
          } else {
            setAddresses([]);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching addresses:", error);
          setError("Error fetching addresses");
          setLoading(false);
        });
    } else {
      setError("No staffId found in localStorage");
      setLoading(false);
    }
  }, [staffId]);

  const handleInputChange = (e) => {
    setNewAddress({
      ...newAddress,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Validate required fields
    if (!newAddress.street || !newAddress.city || !newAddress.state || !newAddress.country || !newAddress.postalCode) {
      setError("Please fill all required fields");
      return;
    }

    if (editMode && selectedAddress) {
      // Update address
      updateAddress(staffId, selectedAddress._id, newAddress)
        .then((response) => {
          if (response.data && response.data.updatedAddress) {
            const updatedAddresses = addresses.map((address) =>
              address._id === selectedAddress._id
                ? response.data.updatedAddress
                : address
            );
            setAddresses(updatedAddresses);
            resetForm();
          } else {
            setError("Invalid response from server");
          }
        })
        .catch((error) => {
          console.error("Error updating address:", error);
          setError("Error updating address");
        });
    } else {
      // Add new address
      createAddress(staffId, newAddress)
        .then((response) => {
          if (response.data && response.data.address) {
            setAddresses([...addresses, response.data.address]);
            resetForm();
          } else {
            setError("Invalid response from server");
          }
        })
        .catch((error) => {
          console.error("Error adding address:", error);
          const server = apiErrorMessage(error, "Error adding address");
          setError(/already exists/i.test(server) ? `${server} Edit the existing address instead.` : server);
        });
    }
  };

  const resetForm = () => {
    setNewAddress({
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      addressType: "",
    });
    setEditMode(false);
    setSelectedAddress(null);
    setIsFormVisible(false);
    setError("");
  };

  const handleEdit = (address) => {
    setNewAddress({
      street: address.street || "",
      city: address.city || "",
      state: address.state || "",
      country: address.country || "",
      postalCode: address.postalCode || "",
      addressType: address.addressType || "",
    });
    setEditMode(true);
    setSelectedAddress(address);
    setIsFormVisible(true);
    setError("");
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    removeAddress(staffId, pendingDeleteId)
      .then(() => {
        setAddresses((prev) => prev.filter((a) => a._id !== pendingDeleteId));
      })
      .catch((error) => {
        console.error("Error removing address:", error);
        setError("Error removing address");
      })
      .finally(() => setPendingDeleteId(null));
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-low">
      <Helmet>
        <title>Address Management | Elthium Health</title>
        <meta name="description" content="Manage your addresses for Elthium Health account" />
        <meta name="keywords" content="address, management, Elthium Health, location" />
      </Helmet>
      
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0">
        <div className="flex-grow px-4 py-6">
          {!isFormVisible ? (
            <>
              {/* List Page */}
              <h2 className="text-xl font-bold mb-6">Addresses</h2>
              {error && <p className="text-red-500 text-center mb-4">{error}</p>}

              {/* Add button */}
              <button
                onClick={() => setIsFormVisible(true)}
                className="flex items-center border border-primary bg-surface-lowest text-primary py-3 px-4 rounded-lg w-full mb-6 font-medium"
              >
                <div className="bg-primary text-white p-2 rounded-full mr-2 flex items-center justify-center">
                  <FaPlus size={16} />
                </div>
                Add Address
              </button>

              {/* Addresses List */}
              {loading && (
                <LoadingState branded message="Loading addresses…" className="py-8" />
              )}
              {addresses.length === 0 && !loading && (
                <EmptyState
                  icon={FaMapMarkerAlt}
                  title="No addresses found yet."
                  description="Add a delivery address for home sample collection and other services."
                  actionLabel="Add Address"
                  onAction={() => setIsFormVisible(true)}
                />
              )}

              <div className="space-y-4">
                {addresses.map((address) => (
                  <div
                    key={address._id}
                    className="bg-surface-lowest p-4 rounded-lg shadow flex justify-between items-center"
                  >
                    {/* Left side */}
                    <div className="flex items-start space-x-3">
                      <FaMapMarkerAlt size={30} className="text-primary" />
                      <div>
                        <p className="font-bold text-on-primary-fixed-var">
                          {address.addressType || "Address"}
                        </p>
                        <p className="text-on-surface-variant">
                          {address.street}, {address.city}, {address.state}
                        </p>
                        <p className="text-on-surface-variant">
                          {address.country} - {address.postalCode}
                        </p>
                      </div>
                    </div>

                    {/* Right side - actions */}
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleEdit(address)}
                        className="text-primary hover:text-primary-container"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() => setPendingDeleteId(address._id)}
                        className="text-red-500 hover:text-error"
                      >
                        <FaTrash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Form Page */}
              <h2 className="text-xl font-bold mb-6">
                {editMode ? "Edit Address" : "Add Address"}
              </h2>
              
              {error && <p className="text-red-500 text-center mb-4">{error}</p>}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="street"
                  value={newAddress.street}
                  onChange={handleInputChange}
                  placeholder="Street *"
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <input
                  type="text"
                  name="city"
                  value={newAddress.city}
                  onChange={handleInputChange}
                  placeholder="City *"
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <input
                  type="text"
                  name="state"
                  value={newAddress.state}
                  onChange={handleInputChange}
                  placeholder="State *"
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <input
                  type="text"
                  name="country"
                  value={newAddress.country}
                  onChange={handleInputChange}
                  placeholder="Country *"
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <input
                  type="text"
                  name="postalCode"
                  value={newAddress.postalCode}
                  onChange={handleInputChange}
                  placeholder="Postal Code *"
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <select
                  name="addressType"
                  value={newAddress.addressType}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Address type</option>
                  {["Home", "Work", "Other"]
                    .filter((type) => {
                      const used = addresses.some(
                        (a) => (a.addressType || "").toLowerCase() === type.toLowerCase()
                          && a._id !== selectedAddress?._id
                      );
                      return !used;
                    })
                    .map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                </select>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-primary text-white py-3 rounded-lg hover:bg-primary transition"
                  >
                    {editMode ? "Update Address" : "Save Address"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 bg-gray-300 text-on-primary-fixed-var py-3 rounded-lg hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <Footer />
      </div>

      <ConfirmModal
        open={!!pendingDeleteId}
        title="Delete this address?"
        message="Are you sure you want to delete this address? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default AddressPage;